import { getStaticPageByName } from "@config";
import PropTypes from "prop-types";
import React, { Component, useState, useEffect } from "react";
import Overridable from "react-overridable";
import { Link, useLocation } from "react-router-dom";
import {
  Container,
  Grid,
  Header,
  List,
  Form,
  TextArea,
  Message,
  Button,
  Icon,
} from "semantic-ui-react";
import { FeedbackService } from "@api/feedback/feedback";
import { useTour } from "@modules/GuidedTour/useTour";
import { TOUR_TYPES } from "@modules/GuidedTour/tourConfigs";
import { FrontSiteRoutes } from "@routes/urls";
import { userApi } from "@api/users/user";
import { authenticationService } from "@authentication/services/AuthenticationService";

const FooterLayout = (props) => {
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", content: "" });
  const [isConnected, setIsConnected] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [isAnonymous, setIsAnonymous] = useState(false);

  // Get current user information with cleanup
  useEffect(() => {
    let isMounted = true; // Flag to track if component is mounted

    const getCurrentUser = async () => {
      try {
        setUserLoading(true);
        const response = await userApi.get();

        // Only update state if component is still mounted
        if (isMounted) {
          if (response.data && response.data.id) {
            setCurrentUser(response.data);

            FeedbackService.setCurrentUser({
              fullName: response.data.full_name,
            });
          } else {
            setCurrentUser(null);
          }
        }
      } catch (error) {
        if (isMounted) {
          setCurrentUser(null);
        }
      } finally {
        if (isMounted) {
          setUserLoading(false);
        }
      }
    };

    getCurrentUser();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);

  const handleFeedbackSubmit = async () => {
    if (!feedbackText.trim()) {
      setMessage({
        type: "error",
        content: "Please enter your feedback before submitting.",
      });
      return;
    }

    setIsSubmitting(true);
    setMessage({ type: "", content: "" });

    try {
      const feedbackOptions = {
        category: "General Feedback",
      };

      const userName = currentUser
        ? currentUser.full_name || "Unknown User"
        : "Unknown User";

      if (isAnonymous) {
        feedbackOptions.createdBy = null;
      } else if (userName !== "Unknown User") {
        feedbackOptions.createdBy = userName;
      } else {
        feedbackOptions.createdBy = null;
      }

      await FeedbackService.submitFeedback(feedbackText, feedbackOptions);

      setMessage({
        type: "success",
        content:
          "Thank you for your feedback! It has been submitted successfully.",
      });
      setFeedbackText("");
      setIsAnonymous(false);
    } catch (error) {
      setMessage({
        type: "error",
        content:
          error.message || "Failed to submit feedback. Please try again.",
      });
      setIsConnected(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTextChange = (e, { value }) => {
    setFeedbackText(value);
    if (message.content) {
      setMessage({ type: "", content: "" });
    }
  };

  const getUserDisplayName = () => {
    if (!currentUser) return "Unknown User";
    return currentUser.full_name || "Unknown User";
  };

  const handleRetryConnection = () => {
    setIsConnected(true);
    setMessage({
      type: "success",
      content: "Connection restored! You can now submit feedback.",
    });
  };

  const canAccessTour = (user) => {
    if (!user) return false;
    return !authenticationService.hasRoles(user, ["admin"]);
  };

  const TourLink = () => {
    const { startTour, activeTour } = useTour();
    const location = useLocation();

    const isDashboard =
      location.pathname === "/dashboard" ||
      location.pathname.startsWith("/dashboard");

    const handleStartTour = () => {
      if (!isDashboard) {
        alert(
          "Please navigate to the Dashboard page to start the guided tour."
        );
        return;
      }
      startTour(TOUR_TYPES.ONBOARDING);
    };

    const isDisabled = activeTour || !isDashboard;

    return (
      <span
        onClick={isDisabled ? undefined : handleStartTour}
        style={{
          color: "#4183C4",
          cursor: isDisabled ? "not-allowed" : "pointer",
          textDecoration: "none",
          opacity: isDisabled ? 0.7 : 1,
        }}
        title={
          activeTour
            ? "Tour is currently running"
            : !isDashboard
            ? "Go to Dashboard to start the guided tour"
            : "Start guided tour of the system"
        }
      >
        User Guided Tour
      </span>
    );
  };

  return (
    <Overridable id="Footer.layout">
      <footer>
        <Container fluid className="footer-upper">
          <Container>
            <Grid columns={4} stackable>
              <Grid.Column>
                <Header
                  as="h4"
                  content="More information"
                  data-tour="footer-info"
                />
                <List>
                  <List.Item>
                    <Link to={getStaticPageByName("about").route}>About</Link>
                  </List.Item>
                  <List.Item>
                    <Link to={getStaticPageByName("contact").route}>
                      Contact
                    </Link>
                  </List.Item>
                  <List.Item>
                    <Link to={FrontSiteRoutes.openingHours}>Opening Hours</Link>
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column>
                <Header as="h4"> Help Links </Header>
                <List>
                  <List.Item>
                    <Link to={getStaticPageByName("search-guide").route}>
                      Search Guide
                    </Link>
                  </List.Item>
                  {canAccessTour(currentUser) && (
                    <List.Item data-tour="user-guide">
                      <TourLink />
                    </List.Item>
                  )}
                  <List.Item>
                    <Link to={FrontSiteRoutes.faqs}>
                      Frequently Asked Questions (FAQs)
                    </Link>
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column>
                <Header as="h4">Connect With Us</Header>
                <List>
                  <List.Item>
                    <a
                      href="mailto:rsulrc2020@gmail.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#4183C4", textDecoration: "none" }}
                    >
                      <Icon name="mail" />
                      Gmail
                    </a>
                  </List.Item>
                  <List.Item>
                    <a
                      href="https://www.facebook.com/RSULRC"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#4183C4", textDecoration: "none" }}
                    >
                      <Icon name="facebook" />
                      Facebook
                    </a>
                  </List.Item>
                </List>
              </Grid.Column>
              <Grid.Column data-tour="footer-feedback">
                <Header>
                  Report Some Feedback Here
                  {!userLoading && !currentUser && (
                    <Header.Subheader
                      style={{ color: "#888", fontSize: "0.8em" }}
                    >
                      Anonymous feedback
                    </Header.Subheader>
                  )}
                </Header>

                {isConnected === false && (
                  <Message warning size="small">
                    <Message.Header>Server Connection Issue</Message.Header>
                    <p>Unable to connect to the feedback service.</p>
                    <Button size="mini" onClick={handleRetryConnection}>
                      Retry Connection
                    </Button>
                  </Message>
                )}

                <Form>
                  <TextArea
                    placeholder={
                      !currentUser
                        ? "Share your feedback... (submitting as anonymous)"
                        : isAnonymous
                        ? "Share your feedback... (submitting anonymously)"
                        : `Share your feedback... (submitting as ${getUserDisplayName()})`
                    }
                    value={feedbackText}
                    onChange={handleTextChange}
                    rows={3}
                    maxLength={5000}
                    disabled={isConnected === false || userLoading}
                  />
                  <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                    <span style={{ color: "#999", fontSize: "0.9em" }}>
                      {feedbackText.length}/5000 characters
                    </span>
                  </div>
                  <Form.Button
                    primary
                    onClick={handleFeedbackSubmit}
                    loading={isSubmitting || userLoading}
                    disabled={
                      isSubmitting ||
                      userLoading ||
                      !feedbackText.trim() ||
                      isConnected === false
                    }
                  >
                    Submit Feedback
                  </Form.Button>
                  {message.content && (
                    <Message
                      positive={message.type === "success"}
                      negative={message.type === "error"}
                      size="small"
                      style={{ marginTop: "10px" }}
                    >
                      {message.content}
                    </Message>
                  )}
                </Form>
              </Grid.Column>
            </Grid>
          </Container>
        </Container>
        <Container fluid className="footer-lower">
          <Container>
            <Header as="h4" textAlign="center">
              <Header.Content>
                Library Information Management System
              </Header.Content>
              <Header.Subheader>
                Powered by{" "}
                <a
                  href="https://inveniosoftware.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="footer-link"
                >
                  INVENIO
                </a>
              </Header.Subheader>
            </Header>
          </Container>
        </Container>
      </footer>
    </Overridable>
  );
};

class ILSFooter extends Component {
  componentDidMount() {
    const apiUrl = process.env.REACT_APP_INVENIO_UI_URL;
    FeedbackService.init(apiUrl);
  }

  render() {
    return <FooterLayout {...this.props} />;
  }
}

ILSFooter.propTypes = {
  staticPages: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired,
      apiURL: PropTypes.string.isRequired,
    })
  ),
};

ILSFooter.defaultProps = {
  staticPages: [],
};

FooterLayout.propTypes = ILSFooter.propTypes;
FooterLayout.defaultProps = ILSFooter.defaultProps;

export default Overridable.component("ILSFooter", ILSFooter);
